import { useImperativeHandle } from "react";
import * as XLSX from "xlsx";

/**
 * usePrint
 * Imperative print API exposed via the ref the parent forwards.
 * Two paths:
 *   - printFromSource: re-parses the original Excel binary and renders
 *     every sheet via XLSX.utils.sheet_to_html.  Preferred — faithful
 *     to the file rather than the on-screen DOM.
 *   - printAsPdf: legacy DOM-clone fallback for screenshot-style output.
 */
export default function usePrint({ ref, workbookBufferRef, componentRef }) {
  useImperativeHandle(ref, () => ({
    printFromSource: () => {
      const buf = workbookBufferRef.current;
      if (!buf) {
        console.warn("[usePrint] printFromSource: raw workbook buffer not available.");
        return;
      }
      let wb;
      try {
        wb = XLSX.read(buf, { type: "array", cellFormula: true, cellStyles: true });
      } catch (err) {
        console.error("[usePrint] printFromSource: failed to parse buffer", err);
        return;
      }

      const sheetSections = wb.SheetNames.map((name, idx) => {
        const ws = wb.Sheets[name];
        const tableHtml = XLSX.utils.sheet_to_html(ws, { id: `sheet-${idx}`, editable: false });
        const pageBreak = idx > 0 ? `<div style="page-break-before: always;"></div>` : "";
        return `${pageBreak}<h3 style="font-family: Calibri, Arial, sans-serif; font-size: 13px; margin: 0 0 4px 0; color: #444;">${name}</h3>${tableHtml}`;
      }).join("");

      openPrintIframe(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Print Excel</title>
        <style>
          body { font-family: Calibri, "Segoe UI", Arial, sans-serif; font-size: 11pt; margin: 0; padding: 10px; color: #000; }
          table { border-collapse: collapse; width: max-content; margin-bottom: 4px; }
          td, th { border: 1px solid #bbb; padding: 2px 5px; font-size: 10pt; white-space: pre-wrap; vertical-align: middle; min-width: 60px; }
          @media print {
            @page { margin: 1cm; size: landscape; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            h3 { margin-top: 0; }
          }
        </style></head><body>${sheetSections}</body></html>`);
    },

    printAsPdf: () => {
      if (!componentRef.current) return;
      const tableClone = componentRef.current.cloneNode(true);
      tableClone.style.zoom = "";
      const tableHTML = tableClone.outerHTML;
      openPrintIframe(`<html><head><title>Print Excel</title>
        <style>
          body { font-family: Calibri, "Segoe UI", Arial, sans-serif; padding: 10px; margin: 0; }
          .simple-excel-grid { border-collapse: collapse; width: max-content; table-layout: auto; }
          .simple-excel-grid th, .simple-excel-grid td {
            border: 1px solid #d0d0d0; padding: 2px 4px; font-size: 12px;
            line-height: 1.4; vertical-align: middle; white-space: nowrap;
          }
          .simple-excel-grid thead th { background: #f3f3f3; font-weight: bold; text-align: center; }
          .simple-excel-grid tbody th { background: #f3f3f3; font-weight: bold; text-align: center; min-width: 44px; width: 44px; }
          .simple-excel-grid td { background: #ffffff; }
          .simple-excel-cell { width: 100%; display: block; overflow: hidden; text-overflow: ellipsis; white-space: inherit; word-break: normal; }
          .filter-excel-btn, .col-resize-handle, .row-resize-handle { display: none !important; }
          @media print {
            @page { margin: 0.5cm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        </style></head><body>${tableHTML}</body></html>`);
    },
  }));
}

/**
 * Builds a hidden iframe with the supplied HTML, calls print(), and
 * cleans up afterwards.  Used by both print paths above.
 */
function openPrintIframe(html) {
  const iframe = document.createElement("iframe");
  Object.assign(iframe.style, {
    position: "fixed", left: "-10000px", top: "-10000px",
    width: "1200px", height: "900px", border: "none", visibility: "hidden",
  });
  document.body.appendChild(iframe);
  const targetDoc = iframe.contentWindow.document;
  targetDoc.open();
  targetDoc.write(html);
  targetDoc.close();

  const removeIframe = () => { try { document.body.removeChild(iframe); } catch (_) {} };
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      console.error("[usePrint] print() failed:", err);
    }
    if (iframe.contentWindow) iframe.contentWindow.addEventListener("afterprint", removeIframe, { once: true });
    setTimeout(removeIframe, 60000);
  }, 400);
}
